export interface HandoffLiver {
  id: string
  display_name?: string | null
  status?: string | null
}

export interface HandoffAssignment {
  id: string
  liver_id: string
  manager_id: string
  started_at: string
  ended_at: string | null
  change_reason: string | null
  predecessor_manager_id: string | null
  handoff_notes: string | null
  created_at: string
  created_by: string | null
  manager?: {
    id: string
    name: string | null
    email: string
    department: string | null
  } | null
  predecessor?: {
    id: string
    name: string | null
    email: string
  } | null
}

export type HandoffRowState = 'assigned' | 'unassigned'

export interface HandoffDashboardRow {
  liverId: string
  liverName: string
  state: HandoffRowState
  assignmentId: string | null
  managerName: string | null
  managerEmail: string | null
  managerDepartment: string | null
  predecessorName: string | null
  startedAt: string | null
  changeReason: string | null
  handoffNotes: string | null
  isRecentChange: boolean
  needsReview: boolean
}

export interface HandoffDashboard {
  activeLiverCount: number
  assignedCount: number
  unassignedCount: number
  recentChangeCount: number
  notesCount: number
  rows: HandoffDashboardRow[]
}

const RECENT_CHANGE_DAYS = 30

export function buildHandoffDashboard(
  livers: HandoffLiver[],
  assignments: HandoffAssignment[],
  now: Date
): HandoffDashboard {
  const activeLivers = livers.filter((liver) => liver.status !== 'graduated')
  const currentByLiver = new Map<string, HandoffAssignment>()
  for (const assignment of assignments) {
    if (assignment.ended_at !== null) continue
    const current = currentByLiver.get(assignment.liver_id)
    if (!current || compareDateDesc(assignment.started_at, current.started_at) < 0) {
      currentByLiver.set(assignment.liver_id, assignment)
    }
  }

  const rows = activeLivers.map((liver) => toRow(liver, currentByLiver.get(liver.id) ?? null, now)).sort(compareRows)

  return {
    activeLiverCount: activeLivers.length,
    assignedCount: rows.filter((row) => row.state === 'assigned').length,
    unassignedCount: rows.filter((row) => row.state === 'unassigned').length,
    recentChangeCount: rows.filter((row) => row.isRecentChange).length,
    notesCount: rows.filter((row) => Boolean(row.handoffNotes)).length,
    rows
  }
}

export function findCurrentAssignmentForLiver<T extends HandoffAssignment>(
  assignments: T[],
  liverId: string
): T | null {
  return assignments
    .filter((assignment) => assignment.liver_id === liverId && assignment.ended_at === null)
    .sort((a, b) => compareDateDesc(a.started_at, b.started_at))[0] ?? null
}

function toRow(liver: HandoffLiver, assignment: HandoffAssignment | null, now: Date): HandoffDashboardRow {
  if (!assignment) {
    return {
      liverId: liver.id,
      liverName: liver.display_name ?? '名称未設定',
      state: 'unassigned',
      assignmentId: null,
      managerName: null,
      managerEmail: null,
      managerDepartment: null,
      predecessorName: null,
      startedAt: null,
      changeReason: null,
      handoffNotes: null,
      isRecentChange: false,
      needsReview: true
    }
  }

  const isRecentChange = daysSince(assignment.started_at, now) <= RECENT_CHANGE_DAYS
  const managerName = assignment.manager?.name ?? assignment.manager?.email ?? null
  const predecessorName = assignment.predecessor?.name ?? assignment.predecessor?.email ?? null

  return {
    liverId: liver.id,
    liverName: liver.display_name ?? '名称未設定',
    state: 'assigned',
    assignmentId: assignment.id,
    managerName,
    managerEmail: assignment.manager?.email ?? null,
    managerDepartment: assignment.manager?.department ?? null,
    predecessorName,
    startedAt: assignment.started_at,
    changeReason: assignment.change_reason,
    handoffNotes: assignment.handoff_notes,
    isRecentChange,
    needsReview: isRecentChange || Boolean(assignment.handoff_notes)
  }
}

function compareRows(a: HandoffDashboardRow, b: HandoffDashboardRow): number {
  return Number(b.state === 'unassigned') - Number(a.state === 'unassigned') ||
    Number(b.needsReview) - Number(a.needsReview) ||
    Number(b.isRecentChange) - Number(a.isRecentChange) ||
    compareNullableDateDesc(a.startedAt, b.startedAt) ||
    a.liverName.localeCompare(b.liverName, 'ja')
}

function compareDateDesc(a: string, b: string): number {
  return new Date(b).getTime() - new Date(a).getTime()
}

function compareNullableDateDesc(a: string | null, b: string | null): number {
  if (a && b) return compareDateDesc(a, b)
  if (a) return -1
  if (b) return 1
  return 0
}

function daysSince(value: string, now: Date): number {
  const time = new Date(value).getTime()
  if (!Number.isFinite(time)) return Number.POSITIVE_INFINITY
  return Math.floor((now.getTime() - time) / 86400000)
}
